import { useMemo, useState, type FormEvent, type JSX } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext.js';
import { ApiError, mensagemDoErro } from '../lib/api.js';
import './TrocarSenha.css';

/**
 * Trocar a própria senha — Épico UX, Frente 1, Story 1.6.
 *
 * Consome o `POST /api/auth/trocar-senha` da 1.4. Serve a dois momentos com a
 * mesma tela:
 *
 * - **primeiro acesso** (`precisaTrocarSenha`): a pessoa entrou com uma senha
 *   que o admin digitou e conhece. Não há saída daqui até trocar;
 * - **troca voluntária**, pelo menu do usuário. Tem "Voltar".
 *
 * ## Por que fora do Layout
 *
 * No primeiro acesso não pode existir menu para clicar. O `RotaProtegida`
 * devolveria a pessoa para cá de qualquer jeito, mas "clico e volto" é uma
 * interface que parece quebrada. Sem casca, a tela diz sozinha que é o único
 * caminho — e na troca voluntária continua fazendo sentido, porque é uma tela
 * de conta, não de trabalho.
 */

/**
 * Espelha `auth/politica-senha.ts` do backend.
 *
 * Cópia consciente: o front não importa do `apps/api`, e `packages/shared` só
 * carrega envelope e códigos de erro hoje. É o **segundo** candidato a mudar de
 * casa junto com `Papel` (o handoff da 1.1 já apontou) — as duas cabem na 1.5.
 *
 * Enquanto for cópia, ela é só conveniência: quem recusa de verdade é o
 * servidor, e a mensagem dele é a que aparece se as duas divergirem.
 */
const MINIMO_SENHA = 12;
const MAXIMO_SENHA_BYTES = 72;

/** O corte do bcrypt é no byte, não no caractere — 20 emojis são 80 bytes. */
function bytes(texto: string): number {
  return new TextEncoder().encode(texto).length;
}

interface Requisito {
  id: string;
  rotulo: string;
  ok: boolean;
}

/**
 * Mensagem no contexto da TROCA.
 *
 * `mensagemDoErro` traduz `AUTH_INVALID_CREDENTIALS` para "E-mail ou senha
 * inválidos" — correto no login e confuso aqui, onde não há e-mail nenhum em
 * jogo. O que falhou foi a senha ATUAL, e a pessoa precisa saber qual dos três
 * campos revisar.
 *
 * Para `VALIDATION_ERROR` devolvemos o texto do servidor: ele já distingue
 * "precisa de pelo menos 12 caracteres" de "precisa ser diferente da atual",
 * e reescrever aqui seria manter duas versões da mesma frase.
 */
export function mensagemDaTroca(erro: unknown): string {
  if (erro instanceof ApiError) {
    if (erro.code === 'AUTH_INVALID_CREDENTIALS') {
      return 'Senha atual incorreta. Confira e tente de novo.';
    }
    if (erro.code === 'VALIDATION_ERROR') return erro.message;
  }
  return mensagemDoErro(erro);
}

export function TrocarSenha(): JSX.Element {
  const { usuario, trocarSenha } = useAuth();
  const navegar = useNavigate();

  const [atual, setAtual] = useState('');
  const [nova, setNova] = useState('');
  const [confirmacao, setConfirmacao] = useState('');
  const [erro, setErro] = useState<unknown>(undefined);
  const [enviando, setEnviando] = useState(false);
  const [pronto, setPronto] = useState(false);

  const obrigatorio = usuario?.precisaTrocarSenha === true;

  const requisitos = useMemo<Requisito[]>(
    () => [
      {
        id: 'minimo',
        rotulo: `Pelo menos ${String(MINIMO_SENHA)} caracteres`,
        ok: nova.length >= MINIMO_SENHA,
      },
      {
        id: 'maximo',
        rotulo: `No máximo ${String(MAXIMO_SENHA_BYTES)} caracteres`,
        ok: nova !== '' && bytes(nova) <= MAXIMO_SENHA_BYTES,
      },
      {
        id: 'diferente',
        rotulo: 'Diferente da senha atual',
        // Só dá para afirmar quando os dois campos têm conteúdo; o servidor é
        // quem decide de verdade, comparando com o hash.
        ok: nova !== '' && atual !== '' && nova !== atual,
      },
      { id: 'confere', rotulo: 'A confirmação confere', ok: nova !== '' && nova === confirmacao },
    ],
    [nova, atual, confirmacao],
  );

  const tudoOk = requisitos.every((r) => r.ok) && atual !== '';

  async function aoEnviar(evento: FormEvent<HTMLFormElement>): Promise<void> {
    evento.preventDefault();
    setErro(undefined);
    setEnviando(true);
    try {
      await trocarSenha(atual, nova);
      /*
       * Não navegamos sozinhos. A troca é uma ação de segurança e a pessoa
       * precisa VER que deu certo — um redirecionamento automático faria a
       * confirmação passar num piscar. Ela clica em "Continuar" quando quiser.
       */
      setPronto(true);
      setAtual('');
      setNova('');
      setConfirmacao('');
    } catch (e) {
      setErro(e);
    } finally {
      setEnviando(false);
    }
  }

  if (pronto) {
    return (
      <div className="troca">
        <div className="troca__cartao cartao">
          <h1 className="troca__titulo">Senha alterada</h1>
          <p className="troca__ok" role="status">
            Pronto. Sua senha foi alterada e <strong>só você a conhece agora</strong>. As outras
            sessões abertas em outros dispositivos foram encerradas — esta continua ativa.
          </p>
          <button
            type="button"
            className="botao botao--primario botao--bloco"
            onClick={() => {
              navegar('/', { replace: true });
            }}
          >
            Continuar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="troca">
      <div className="troca__cartao cartao">
        <h1 className="troca__titulo">{obrigatorio ? 'Defina sua senha' : 'Trocar senha'}</h1>

        {obrigatorio ? (
          <p className="troca__aviso" role="status">
            Sua senha atual foi definida por quem criou seu acesso, então{' '}
            <strong>mais de uma pessoa a conhece</strong>. Escolha uma senha nova para continuar.
          </p>
        ) : (
          <p className="troca__sub">
            Você precisa da senha atual para confirmar que é você. As sessões abertas em outros
            dispositivos serão encerradas.
          </p>
        )}

        <form
          className="troca__form"
          onSubmit={(e) => {
            void aoEnviar(e);
          }}
          noValidate
        >
          <label className="campo">
            <span className="campo__rotulo">Senha atual</span>
            <input
              className="campo__entrada"
              type="password"
              value={atual}
              onChange={(e) => {
                setAtual(e.target.value);
              }}
              autoComplete="current-password"
              autoFocus
              required
            />
          </label>

          <label className="campo">
            <span className="campo__rotulo">Nova senha</span>
            <input
              className="campo__entrada"
              type="password"
              value={nova}
              onChange={(e) => {
                setNova(e.target.value);
              }}
              autoComplete="new-password"
              required
            />
          </label>

          <label className="campo">
            <span className="campo__rotulo">Confirme a nova senha</span>
            <input
              className="campo__entrada"
              type="password"
              value={confirmacao}
              onChange={(e) => {
                setConfirmacao(e.target.value);
              }}
              autoComplete="new-password"
              required
            />
          </label>

          {/*
            A lista aparece assim que a pessoa começa a digitar a nova senha.
            Mostrá-la antes disso é ruído; escondê-la até o erro faria a pessoa
            descobrir a regra errando.
          */}
          {nova !== '' && (
            <ul className="troca__regras" aria-label="Requisitos da nova senha">
              {requisitos.map((r) => (
                <li
                  key={r.id}
                  className={r.ok ? 'troca__regra troca__regra--ok' : 'troca__regra'}
                  data-ok={r.ok ? 'sim' : 'nao'}
                >
                  <span className="troca__marca" aria-hidden="true">
                    {r.ok ? '✓' : '·'}
                  </span>
                  {r.rotulo}
                </li>
              ))}
            </ul>
          )}

          {erro !== undefined && (
            <p className="aviso" role="alert">
              {mensagemDaTroca(erro)}
            </p>
          )}

          <button
            type="submit"
            className="botao botao--primario botao--bloco"
            disabled={enviando || !tudoOk}
          >
            {enviando ? 'Salvando…' : 'Salvar nova senha'}
          </button>

          {/*
            Sem saída no primeiro acesso: o botão não existe, e o RotaProtegida
            devolveria a pessoa para cá de qualquer forma. Ter um "Voltar" que
            não volta seria pior que não ter.
          */}
          {!obrigatorio && (
            <button
              type="button"
              className="botao botao--discreto botao--bloco"
              onClick={() => {
                navegar(-1);
              }}
              disabled={enviando}
            >
              Voltar
            </button>
          )}
        </form>
      </div>
    </div>
  );
}
